import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Loader2, FileText, Trash2, Heart, Search as SearchIcon, Bell, BellOff, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import ListingCard from "../ListingCard";
import NotificationPermissionBanner from "../NotificationPermissionBanner";
import CollapsibleWidget from "./CollapsibleWidget";

/**
 * Aus `DashboardPage.jsx` ausgelagerte, in sich geschlossene Tab-Komponenten
 * (Entwürfe / Favoriten / Gespeicherte Suchen) — Teil der Modularisierung (P2).
 * Reine Props-/Self-Fetch-Komponenten ohne Bezug auf den DashboardPage-State.
 */

export function MyDrafts({ userId }) {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const merged = new Map();
        // 1) Backend drafts (primary, cross-device)
        try {
            const res = await api.get("/listing-drafts");
            for (const d of res.data.items || []) {
                const savedAt = new Date(d.updated_at || d.created_at);
                merged.set(d.id, { key: d.id, id: d.id, savedAt, form: d.form || {}, source: "backend" });
            }
        } catch {
            /* offline — show localStorage only */
        }
        // 2) localStorage fallback (offline drafts not yet synced)
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key || !key.startsWith("tw_create_listing_draft_v1_")) continue;
                if (userId && !key.endsWith(`_${userId}`)) continue;
                try {
                    const parsed = JSON.parse(localStorage.getItem(key));
                    if (parsed?.form && parsed?.savedAt) {
                        const id = parsed.id;
                        // Skip if already loaded from backend with same id
                        if (id && merged.has(id)) continue;
                        merged.set(key, {
                            key,
                            id: id || null,
                            savedAt: new Date(parsed.savedAt),
                            form: parsed.form,
                            source: "local",
                        });
                    }
                } catch { /* corrupted entry, skip */ }
            }
        } catch { /* storage disabled */ }
        const list = Array.from(merged.values()).sort((a, b) => b.savedAt - a.savedAt);
        setDrafts(list);
        setLoading(false);
    }, [userId]);

    useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

    const discard = async (item) => {
        if (item.source === "backend" && item.id) {
            try { await api.delete(`/listing-drafts/${item.id}`); } catch { /* noop */ }
        }
        try {
            // also wipe matching localStorage entry
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key) continue;
                if (key === item.key || (item.id && key.startsWith("tw_create_listing_draft_v1_"))) {
                    try {
                        const parsed = JSON.parse(localStorage.getItem(key));
                        if (!item.id || parsed?.id === item.id) localStorage.removeItem(key);
                    } catch { /* noop */ }
                }
            }
        } catch { /* noop */ }
        load();
    };
    const resume = () => {
        // CreateListingPage erkennt den Backend-/Local-Draft beim Mount automatisch.
        window.location.assign("/create-listing");
    };

    if (loading) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-8 text-center" data-testid="drafts-loading">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
            </div>
        );
    }

    if (drafts.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-8 text-center" data-testid="drafts-empty">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                <div className="font-bold text-sm mb-1">Keine Entwürfe</div>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                    Sobald du auf &bdquo;Inserat erstellen&ldquo; anfängst, speichern wir deinen Fortschritt automatisch alle 30 Sekunden. Hier findest du sie dann wieder.
                </p>
                <Button onClick={() => window.location.assign("/create-listing")} className="mt-4 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider text-xs h-9" data-testid="drafts-empty-create-btn">
                    Inserat erstellen
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="drafts-list">
            {drafts.map((d) => {
                const f = d.form || {};
                const titleLine = f.title || [f.brand, f.model].filter(Boolean).join(" ") || "Unbenannter Entwurf";
                const km = Number(f.mileage) || 0;
                const ezYear = f.first_registration_date ? new Date(f.first_registration_date).getFullYear() : f.year;
                return (
                    <div key={d.key} className="bg-white border border-gray-200 rounded-sm p-4 flex items-start gap-4" data-testid="draft-item">
                        <div className="w-14 h-14 bg-amber-50 border border-amber-200 rounded-sm flex items-center justify-center shrink-0">
                            <FileText className="w-6 h-6 text-amber-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm leading-tight">{titleLine}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                                {[f.brand, f.model].filter(Boolean).join(" · ") || "—"}
                                {ezYear && ` · EZ ${ezYear}`}
                                {km > 0 && ` · ${km.toLocaleString("de-DE")} km`}
                                {f.price && ` · ${Number(f.price).toLocaleString("de-DE")} €`}
                            </div>
                            <div className="text-[10px] text-gray-400 mt-1">
                                Zuletzt gespeichert: {d.savedAt.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} Uhr
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                            <Button onClick={resume} className="h-8 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider text-[10px] px-3" data-testid="draft-resume-btn">
                                Fortsetzen
                            </Button>
                            <Button onClick={() => discard(d)} variant="outline" className="h-8 rounded-sm uppercase tracking-wider text-[10px] px-3 border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="draft-discard-btn">
                                <Trash2 className="w-3 h-3 mr-1" /> Verwerfen
                            </Button>
                        </div>
                    </div>
                );
            })}
            <div className="text-[10px] text-gray-400 mt-2 text-center">
                Entwürfe werden geräteübergreifend in der Cloud gespeichert und lokal als Fallback. Nach 24 Stunden ohne Aktivität werden lokale Kopien verworfen.
            </div>
        </div>
    );
}

// Jun-2026 (User-Wunsch) — Favorisiertes Inserat mit kleinem Änderungs-Marker
// („Verkauft" oder „Preis gesenkt −X €"), damit man auf einen Blick sieht,
// bei welchem beobachteten Fahrzeug sich etwas getan hat.
function FavoriteListingCard({ listing, onToggleFavorite, isFavorited }) {
    const sold = listing.sold === true;
    const drop = listing.fav_price_drop;
    const dropAmount = drop ? Math.round(drop.from - drop.to) : 0;
    return (
        <div data-testid={`favorite-item-${listing.id}`}>
            {sold ? (
                <div className="mb-1.5 inline-flex items-center gap-1 bg-[#DC2626] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm" data-testid={`fav-marker-sold-${listing.id}`}>
                    Verkauft
                </div>
            ) : drop && dropAmount > 0 ? (
                <div className="mb-1.5 inline-flex items-center gap-1 bg-green-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm font-mono-num" data-testid={`fav-marker-pricedrop-${listing.id}`}>
                    Preis gesenkt −{dropAmount.toLocaleString("de-DE")} €
                </div>
            ) : null}
            <ListingCard listing={listing} onToggleFavorite={onToggleFavorite} isFavorited={isFavorited} />
        </div>
    );
}


export function MyFavorites() {
    const [items, setItems] = useState([]);
    const [favIds, setFavIds] = useState([]);

    const load = useCallback(async () => {
        const { data } = await api.get("/favorites");
        setItems(data);
        setFavIds(data.map((d) => d.id));
    }, []);

    useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

    const toggle = async (id) => {
        await api.post(`/favorites/${id}`);
        load();
    };

    if (items.length === 0) {
        return (
            <CollapsibleWidget collapseKey="favorites" title="Favoriten / Merkliste" icon={Heart} count={0} defaultCollapsed testid="favorites-widget">
                <div className="text-center py-8" data-testid="favorites-empty">
                    <Heart className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600">Noch keine Favoriten gespeichert.</p>
                </div>
            </CollapsibleWidget>
        );
    }
    return (
        <CollapsibleWidget collapseKey="favorites" title="Favoriten / Merkliste" icon={Heart} count={items.length} defaultCollapsed testid="favorites-widget">
            <div className="space-y-4">
                {items.map((l) => (
                    <FavoriteListingCard key={l.id} listing={l} onToggleFavorite={toggle} isFavorited={favIds.includes(l.id)} />
                ))}
            </div>
        </CollapsibleWidget>
    );
}

export function MySavedSearches() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [runningId, setRunningId] = useState(null);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const { data } = await api.get("/saved-searches");
            setItems(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        // Reset the header pill counter — the user has now seen the matches.
        // Best-effort: failures are silent (pill will sync on next WS event).
        api.post("/saved-searches/mark-seen").catch(() => { /* noop */ });
    }, []);

    const togglePause = async (s) => {
        try {
            await api.patch(`/saved-searches/${s.id}`, { paused: !s.paused });
            load();
        } catch {
            toast.error("Speichern fehlgeschlagen");
        }
    };

    const setFrequency = async (s, freq) => {
        try {
            await api.patch(`/saved-searches/${s.id}`, { frequency: freq });
            load();
        } catch {
            toast.error("Speichern fehlgeschlagen");
        }
    };

    const remove = async (sid) => {
        if (!window.confirm("Diese gespeicherte Suche wirklich löschen?")) return;
        try {
            await api.delete(`/saved-searches/${sid}`);
            toast.success("Gelöscht");
            load();
        } catch {
            toast.error("Löschen fehlgeschlagen");
        }
    };

    const runNow = async (s) => {
        setRunningId(s.id);
        try {
            const { data } = await api.post(`/saved-searches/${s.id}/run`);
            const n = data.new_matches || 0;
            if (n === 0) {
                toast.info(`Keine neuen Treffer für „${s.name}"`);
            } else {
                toast.success(`${n} neue Treffer für „${s.name}"`, {
                    action: { label: "Öffnen", onClick: () => openSearch(s) },
                });
            }
        } catch {
            toast.error("Manueller Lauf fehlgeschlagen");
        } finally {
            setRunningId(null);
        }
    };

    const openSearch = (s) => {
        const params = new URLSearchParams();
        if (s.category) params.set("category", s.category);
        ["brand", "condition", "q"].forEach((k) => { if (s[k]) params.set(k, s[k]); });
        ["min_price", "max_price", "min_year", "min_mileage", "max_mileage"].forEach((k) => {
            if (s[k] !== null && s[k] !== undefined) params.set(k, s[k]);
        });
        navigate(`/search?${params.toString()}`);
    };

    if (loading) return <div className="text-sm text-gray-500">Lädt…</div>;

    return (
        <CollapsibleWidget collapseKey="saved_searches" title="Gespeicherte Suchen" icon={SearchIcon} count={items.length} defaultCollapsed testid="saved-searches-widget">
            {items.length === 0 ? (
                <div className="text-center py-6" data-testid="saved-searches-empty">
                    <SearchIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <h3 className="font-bold">Noch keine gespeicherten Suchen</h3>
                    <p className="text-sm text-gray-500 mt-1">Auf der Suche-Seite finden Sie oben rechts den Button <strong>&bdquo;Suche speichern&ldquo;</strong>. Bei neuen Treffern erhalten Sie eine E-Mail.</p>
                    <Button onClick={() => navigate("/search?category=motorcycle")} className="mt-4 bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider" data-testid="open-search-btn">
                        Zur Suche
                    </Button>
                </div>
            ) : (
                <div className="space-y-3" data-testid="saved-searches-list">
                    <NotificationPermissionBanner />
                    {items.map((s) => {
                        const filterSummary = [];
                        if (s.brand) filterSummary.push(`Marke: ${s.brand}`);
                        if (s.min_price || s.max_price) filterSummary.push(`Preis: ${s.min_price || 0}–${s.max_price || "∞"} €`);
                        if (s.min_mileage || s.max_mileage) filterSummary.push(`KM: ${s.min_mileage || 0}–${s.max_mileage || "∞"}`);
                        if (s.min_year) filterSummary.push(`ab ${s.min_year}`);
                        if (s.condition) filterSummary.push(s.condition);
                        if (s.q) filterSummary.push(`„${s.q}"`);
                        return (
                            <div key={s.id} className={`bg-white border rounded-sm p-4 ${s.paused ? "border-gray-200 opacity-60" : "border-gray-200"}`} data-testid={`saved-search-${s.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-bold text-base">{s.name}</h3>
                                            <Badge className="bg-[#111827] text-white hover:bg-[#111827] uppercase text-[10px] font-bold rounded-sm tracking-wider">
                                                {s.category === "motorcycle" ? "Motorrad" : s.category === "bicycle" ? "Fahrrad" : "Alle"}
                                            </Badge>
                                            {s.paused && <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100 uppercase text-[10px] font-bold rounded-sm">Pausiert</Badge>}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1 truncate">{filterSummary.join(" · ") || "Keine Zusatzfilter"}</div>
                                        <div className="text-xs text-gray-500 mt-2 flex items-center gap-4">
                                            <span>{s.match_count} aktuelle Treffer</span>
                                            {s.last_notified_at && <span>Letzte Mail: {new Date(s.last_notified_at).toLocaleString("de-DE")}</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <select
                                            value={s.frequency}
                                            onChange={(e) => setFrequency(s, e.target.value)}
                                            className="text-xs border border-gray-300 rounded-sm px-2 h-8 font-bold"
                                            data-testid={`freq-${s.id}`}
                                        >
                                            <option value="instant">Sofort</option>
                                            <option value="daily">Täglich</option>
                                            <option value="weekly">Wöchentlich</option>
                                        </select>
                                        <Button onClick={() => togglePause(s)} variant="outline" size="sm" className="h-8 rounded-sm" data-testid={`toggle-${s.id}`}>
                                            {s.paused ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                                        </Button>
                                        <Button onClick={() => runNow(s)} disabled={runningId === s.id || s.paused} variant="outline" size="sm" className="h-8 rounded-sm font-bold text-xs" data-testid={`run-${s.id}`} title="Jetzt prüfen">
                                            {runningId === s.id
                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                : <RefreshCw className="w-3.5 h-3.5" />}
                                        </Button>
                                        <Button onClick={() => openSearch(s)} variant="outline" size="sm" className="h-8 rounded-sm font-bold text-xs" data-testid={`open-${s.id}`}>
                                            Öffnen
                                        </Button>
                                        <Button onClick={() => remove(s.id)} variant="outline" size="sm" className="h-8 rounded-sm border-[#DC2626] text-[#DC2626] hover:bg-red-50" data-testid={`delete-${s.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </CollapsibleWidget>
    );
}
